/**
 * FL-246 Phase 8: 週間スケジュール入力フォーム
 *
 * 週間（月〜日）の稼働スケジュールを一括入力するフォームコンポーネント
 * - 各曜日ごとに稼働有無・稼働時間・備考を入力
 * - バリデーション（時間形式、開始 < 終了時刻）
 * - ダークモード対応
 *
 * Phase 8.1 実装：基本UI・入力フォーム
 */

import React, { useCallback } from 'react';
import { DailyScheduleInput } from '@/types/schedule';

interface WeeklyScheduleFormProps {
  schedules: DailyScheduleInput[];
  onScheduleChange: (schedules: DailyScheduleInput[]) => void;
  disabled?: boolean;
}

/**
 * 曜日名のマッピング
 */
const WEEKDAY_NAMES = ['月', '火', '水', '木', '金', '土', '日'] as const;

/**
 * 日付から曜日インデックスを取得（月曜日=0、日曜日=6）
 */
const getWeekdayIndex = (dateStr: string): number => {
  const date = new Date(dateStr);
  const day = date.getDay();
  return day === 0 ? 6 : day - 1; // 日曜日を6に変換
};

/**
 * 日付を MM/DD 形式でフォーマット
 */
const formatDateShort = (dateStr: string): string => {
  const date = new Date(dateStr);
  const month = date.getMonth() + 1;
  const day = date.getDate();
  return `${month}/${day}`;
};

/**
 * 時刻入力のバリデーション（HH:mm形式）
 */
const isValidTimeFormat = (time: string | undefined): boolean => {
  if (!time) return true; // 未入力はOK
  return /^([0-1][0-9]|2[0-3]):[0-5][0-9]$/.test(time);
};

/**
 * 時刻の大小比較（start < end）
 */
const isValidTimeRange = (timeStart: string | undefined, timeEnd: string | undefined): boolean => {
  if (!timeStart || !timeEnd) return true; // どちらか未入力はスキップ
  return timeStart < timeEnd;
};

/**
 * 週間スケジュール入力フォームコンポーネント
 */
export const WeeklyScheduleForm: React.FC<WeeklyScheduleFormProps> = ({
  schedules,
  onScheduleChange,
  disabled = false,
}) => {
  /**
   * 稼働チェックボックス変更ハンドラー
   */
  const handleWorkingToggle = useCallback(
    (index: number, isWorking: boolean) => {
      const updated = [...schedules];
      updated[index] = {
        ...updated[index],
        isWorking,
        // 稼働しない場合は時刻をクリア
        timeStart: isWorking ? updated[index].timeStart || '08:00' : undefined,
        timeEnd: isWorking ? updated[index].timeEnd || '18:00' : undefined,
      };
      onScheduleChange(updated);
    },
    [schedules, onScheduleChange]
  );

  /**
   * 開始時刻変更ハンドラー
   */
  const handleTimeStartChange = useCallback(
    (index: number, timeStart: string) => {
      const updated = [...schedules];
      updated[index] = {
        ...updated[index],
        timeStart: timeStart || undefined,
      };
      onScheduleChange(updated);
    },
    [schedules, onScheduleChange]
  );

  /**
   * 終了時刻変更ハンドラー
   */
  const handleTimeEndChange = useCallback(
    (index: number, timeEnd: string) => {
      const updated = [...schedules];
      updated[index] = {
        ...updated[index],
        timeEnd: timeEnd || undefined,
      };
      onScheduleChange(updated);
    },
    [schedules, onScheduleChange]
  );

  /**
   * 備考変更ハンドラー
   */
  const handleNotesChange = useCallback(
    (index: number, notes: string) => {
      const updated = [...schedules];
      updated[index] = {
        ...updated[index],
        notes: notes || undefined,
      };
      onScheduleChange(updated);
    },
    [schedules, onScheduleChange]
  );

  return (
    <div className='overflow-x-auto'>
      <table className='w-full border-collapse'>
        <thead>
          <tr className='bg-gray-100 dark:bg-gray-800'>
            <th className='p-3 text-left text-sm font-semibold text-gray-900 dark:text-white border-b border-gray-200 dark:border-gray-700'>
              日付
            </th>
            <th className='p-3 text-left text-sm font-semibold text-gray-900 dark:text-white border-b border-gray-200 dark:border-gray-700'>
              曜日
            </th>
            <th className='p-3 text-center text-sm font-semibold text-gray-900 dark:text-white border-b border-gray-200 dark:border-gray-700'>
              稼働
            </th>
            <th className='p-3 text-left text-sm font-semibold text-gray-900 dark:text-white border-b border-gray-200 dark:border-gray-700'>
              開始時刻
            </th>
            <th className='p-3 text-left text-sm font-semibold text-gray-900 dark:text-white border-b border-gray-200 dark:border-gray-700'>
              終了時刻
            </th>
            <th className='p-3 text-left text-sm font-semibold text-gray-900 dark:text-white border-b border-gray-200 dark:border-gray-700'>
              備考
            </th>
          </tr>
        </thead>
        <tbody>
          {schedules.map((schedule, index) => {
            const weekdayIndex = getWeekdayIndex(schedule.date);
            const weekdayName = WEEKDAY_NAMES[weekdayIndex];
            const isWeekend = weekdayIndex === 5 || weekdayIndex === 6; // 土日
            const hasTimeStartError = !isValidTimeFormat(schedule.timeStart);
            const hasTimeEndError = !isValidTimeFormat(schedule.timeEnd);
            const hasRangeError = !isValidTimeRange(schedule.timeStart, schedule.timeEnd);

            return (
              <tr
                key={schedule.date}
                className={`border-b border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-800 ${
                  isWeekend ? 'bg-blue-50 dark:bg-blue-900/20' : ''
                }`}
              >
                {/* 日付 */}
                <td className='p-3 text-sm text-gray-900 dark:text-white'>
                  {formatDateShort(schedule.date)}
                </td>

                {/* 曜日 */}
                <td
                  className={`p-3 text-sm font-medium ${
                    isWeekend ? 'text-blue-700 dark:text-blue-400' : 'text-gray-900 dark:text-white'
                  }`}
                >
                  {weekdayName}
                </td>

                {/* 稼働チェックボックス */}
                <td className='p-3 text-center'>
                  <input
                    type='checkbox'
                    checked={schedule.isWorking}
                    onChange={e => handleWorkingToggle(index, e.target.checked)}
                    disabled={disabled}
                    className='h-5 w-5 rounded border-gray-300 dark:border-gray-600 text-blue-600 focus:ring-blue-500 disabled:opacity-50 cursor-pointer disabled:cursor-not-allowed'
                  />
                </td>

                {/* 開始時刻 */}
                <td className='p-3'>
                  <input
                    type='time'
                    value={schedule.timeStart || ''}
                    onChange={e => handleTimeStartChange(index, e.target.value)}
                    disabled={disabled || !schedule.isWorking}
                    className={`w-32 px-3 py-2 text-sm border rounded-lg focus:outline-none focus:ring-2
                      ${
                        hasTimeStartError
                          ? 'border-red-500 focus:ring-red-500'
                          : 'border-gray-300 dark:border-gray-600 focus:ring-blue-500'
                      }
                      bg-white dark:bg-gray-900 text-gray-900 dark:text-white
                      disabled:opacity-50 disabled:cursor-not-allowed`}
                  />
                  {hasTimeStartError && schedule.isWorking && (
                    <p className='text-xs text-red-600 dark:text-red-400 mt-1'>形式: HH:mm</p>
                  )}
                </td>

                {/* 終了時刻 */}
                <td className='p-3'>
                  <input
                    type='time'
                    value={schedule.timeEnd || ''}
                    onChange={e => handleTimeEndChange(index, e.target.value)}
                    disabled={disabled || !schedule.isWorking}
                    className={`w-32 px-3 py-2 text-sm border rounded-lg focus:outline-none focus:ring-2
                      ${
                        hasTimeEndError || hasRangeError
                          ? 'border-red-500 focus:ring-red-500'
                          : 'border-gray-300 dark:border-gray-600 focus:ring-blue-500'
                      }
                      bg-white dark:bg-gray-900 text-gray-900 dark:text-white
                      disabled:opacity-50 disabled:cursor-not-allowed`}
                  />
                  {hasTimeEndError && schedule.isWorking && (
                    <p className='text-xs text-red-600 dark:text-red-400 mt-1'>形式: HH:mm</p>
                  )}
                  {!hasTimeEndError && hasRangeError && schedule.isWorking && (
                    <p className='text-xs text-red-600 dark:text-red-400 mt-1'>開始 &lt; 終了</p>
                  )}
                </td>

                {/* 備考 */}
                <td className='p-3'>
                  <input
                    type='text'
                    value={schedule.notes || ''}
                    onChange={e => handleNotesChange(index, e.target.value)}
                    disabled={disabled || !schedule.isWorking}
                    placeholder='備考'
                    className='w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white dark:bg-gray-900 text-gray-900 dark:text-white disabled:opacity-50 disabled:cursor-not-allowed'
                  />
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
};
